import React, { useState } from 'react'
import { Calendar, MapPin, Users, Clock, Camera, Send } from 'lucide-react'

export const QuotePage: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    eventType: '',
    eventDate: '',
    venue: '',
    venueLocation: '',
    guestCount: '',
    budget: '',
    additionalInfo: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [errorMessage, setErrorMessage] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitStatus('idle')
    setErrorMessage('')

    try {
      const response = await fetch('/api/quote', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          phone: formData.phone || null,
          eventDate: formData.eventDate || null,
          eventLocation: formData.venueLocation || null,
          eventType: formData.eventType || null,
          guestCount: formData.guestCount ? parseInt(formData.guestCount) : null,
          message: `Venue: ${formData.venue || 'Not specified'}\nBudget: ${formData.budget || 'Not specified'}\n\nAdditional Information:\n${formData.additionalInfo || 'None provided'}`
        }),
      })

      if (response.ok) {
        setSubmitStatus('success')
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          eventType: '',
          eventDate: '',
          venue: '',
          venueLocation: '',
          guestCount: '',
          budget: '',
          additionalInfo: ''
        })
      } else {
        const errorData = await response.json()
        throw new Error(errorData.error || 'Failed to submit quote request')
      }
    } catch (error) {
      console.error('Error submitting quote request:', error)
      setSubmitStatus('error')
      setErrorMessage(error instanceof Error ? error.message : 'Failed to submit quote request')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  return (
    <div className="min-h-screen">
      {/* Header */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-light text-gray-900 mb-6 tracking-wide">
            Request a Quote
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed mb-8">
            Tell us about your special day and we'll create a personalized photography package for you
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <Clock className="w-8 h-8 text-gray-600 mx-auto mb-3" />
              <h3 className="font-medium text-gray-900 mb-1">Quick Response</h3>
              <p className="text-sm text-gray-600">24-hour reply guarantee</p>
            </div>
            <div>
              <Camera className="w-8 h-8 text-gray-600 mx-auto mb-3" />
              <h3 className="font-medium text-gray-900 mb-1">Custom Packages</h3>
              <p className="text-sm text-gray-600">Tailored to your needs</p>
            </div>
            <div>
              <Users className="w-8 h-8 text-gray-600 mx-auto mb-3" />
              <h3 className="font-medium text-gray-900 mb-1">Personal Consultation</h3>
              <p className="text-sm text-gray-600">Free planning session</p>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Form */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <form onSubmit={handleSubmit} className="bg-white p-8 shadow-sm border border-gray-100 space-y-8">
            
            {/* Contact Information */}
            <div>
              <h2 className="text-2xl font-light text-gray-900 mb-6 flex items-center">
                <Users className="w-6 h-6 mr-3" />
                Contact Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                    First Name *
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Event Details */}
            <div>
              <h2 className="text-2xl font-light text-gray-900 mb-6 flex items-center">
                <Calendar className="w-6 h-6 mr-3" />
                Event Details
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="eventType" className="block text-sm font-medium text-gray-700 mb-2">
                    Event Type *
                  </label>
                  <select
                    id="eventType"
                    name="eventType"
                    value={formData.eventType}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  >
                    <option value="">Select event type</option>
                    <option value="wedding">Wedding</option>
                    <option value="engagement">Engagement Session</option>
                    <option value="elopement">Elopement</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="eventDate" className="block text-sm font-medium text-gray-700 mb-2">
                    Event Date *
                  </label>
                  <input
                    type="date"
                    id="eventDate"
                    name="eventDate"
                    value={formData.eventDate}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  />
                </div>
                <div>
                  <label htmlFor="venue" className="block text-sm font-medium text-gray-700 mb-2">
                    Venue Name
                  </label>
                  <input
                    type="text"
                    id="venue"
                    name="venue"
                    value={formData.venue}
                    onChange={handleChange}
                    placeholder="e.g., Grace Cathedral"
                    className="w-full px-4 py-3 border border-gray-300 focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  />
                </div>
                <div>
                  <label htmlFor="venueLocation" className="block text-sm font-medium text-gray-700 mb-2">
                    Venue Location *
                  </label>
                  <input
                    type="text"
                    id="venueLocation"
                    name="venueLocation"
                    value={formData.venueLocation}
                    onChange={handleChange}
                    required
                    placeholder="City, State"
                    className="w-full px-4 py-3 border border-gray-300 focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  />
                </div>
                <div>
                  <label htmlFor="guestCount" className="block text-sm font-medium text-gray-700 mb-2">
                    Estimated Guest Count
                  </label>
                  <input
                    type="number"
                    id="guestCount"
                    name="guestCount"
                    value={formData.guestCount}
                    onChange={handleChange}
                    placeholder="e.g., 150"
                    className="w-full px-4 py-3 border border-gray-300 focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Additional Information */}
            <div>
              <h2 className="text-2xl font-light text-gray-900 mb-6">Additional Information</h2>
              <div className="space-y-6">
                <div>
                  <label htmlFor="budget" className="block text-sm font-medium text-gray-700 mb-2">
                    Photography Budget
                  </label>
                  <select
                    id="budget"
                    name="budget"
                    value={formData.budget}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  >
                    <option value="">Select budget range</option>
                    <option value="under-3000">Under $3,000</option>
                    <option value="3000-5000">$3,000 - $5,000</option>
                    <option value="5000-8000">$5,000 - $8,000</option>
                    <option value="8000-12000">$8,000 - $12,000</option>
                    <option value="12000-plus">$12,000+</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="additionalInfo" className="block text-sm font-medium text-gray-700 mb-2">
                    Tell us more about your vision
                  </label>
                  <textarea
                    id="additionalInfo"
                    name="additionalInfo"
                    value={formData.additionalInfo}
                    onChange={handleChange}
                    rows={6}
                    placeholder="Share details about your wedding vision, style preferences, special moments you want captured..."
                    className="w-full px-4 py-3 border border-gray-300 focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="text-center pt-6">
              <button
                type="submit"
                disabled={isSubmitting}
                className={`px-12 py-4 font-light tracking-wide transition-colors duration-300 flex items-center justify-center mx-auto space-x-3 ${
                  isSubmitting 
                    ? 'bg-gray-400 text-white cursor-not-allowed' 
                    : 'bg-gray-900 text-white hover:bg-gray-800'
                }`}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    <span>Submit Quote Request</span>
                  </>
                )}
              </button>
              
              {submitStatus === 'idle' && (
                <p className="text-sm text-gray-600 mt-4">
                  We'll respond within 24 hours with a personalized proposal
                </p>
              )}

              {/* Success Message */}
              {submitStatus === 'success' && (
                <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded text-center">
                  <p className="text-green-800 font-medium mb-2">
                    Thank you for your quote request!
                  </p>
                  <p className="text-green-700 text-sm">
                    We've received your information and will respond within 24 hours with a personalized proposal. 
                    In the meantime, feel free to explore our portfolio or contact us directly if you have any questions.
                  </p>
                </div>
              )}

              {/* Error Message */}
              {submitStatus === 'error' && (
                <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded text-center">
                  <p className="text-red-800 font-medium mb-2">
                    There was an error submitting your quote request
                  </p>
                  <p className="text-red-700 text-sm">
                    {errorMessage || 'Please try again or contact us directly for assistance.'}
                  </p>
                </div>
              )}
            </div>
          </form>
        </div>
      </section>
    </div>
  )
}
